import {isRejected, ThunkMiddleware} from '@reduxjs/toolkit'

import {ApigwResponse} from '@shared/helpers/api'

import {ErrorType, GwErrorName, ReasonType, UserAuthStatus, UserAuthStatusType} from '@web/core/constants'
import {PAY_M, PC_PAY, getLoginUrl, getRealNameCheckPage} from '@web/core/urls'
import {getGwErrorMessage, isApiAsyncThunkAuthRejected} from '@web/helpers/api'
import {isMobileDisplay} from '@web/helpers/userAgent'
import {orderInterrupted} from '@web/middlewares/orderInterrupted'
import {commonSelector, CommonState} from '@web/slices/common/slice'
import {setFailureException} from '@web/slices/error/slice'

import {AuthCreatorProps} from './interfaces'

/**
 * T Generic 에는 App 이 가진 Store Type
 */
const authCreator =
    <
        AppState extends {
            common: CommonState
        },
    >({
        svcType = 64,
        noLoginInfoHandler,
        transactionStopHandler,
        validationFailureHandler,
        validationFailureCodes = ['VALIDATION_FAILED'],
        exceptionAuthActionTypes = [],
        exceptionUserAuthStatusList = [],
    }: AuthCreatorProps<AppState>): ThunkMiddleware<AppState> =>
    ({getState}) =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (next: (action: any) => any) =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (action: any) => {
        function authCallback(error: {
            data: {
                code: string
                message: string
                //  eslint-disable-next-line @typescript-eslint/no-explicit-any
                result: any
            }
        }) {
            const state = getState()

            const shouldCheckAnonymousUser = exceptionUserAuthStatusList.includes('ANONYMOUS')

            const {
                data: {code, message: reason, result},
            } = error
            const userAuthStatus = result?.userAuthStatus ?? UserAuthStatus.CERTIFICATE
            const isAnonymousUser = userAuthStatus === UserAuthStatus.ANONYMOUS

            if (code === ApigwResponse.NOT_AUTHORIZED) {
                const url = isMobileDisplay ? `${PAY_M}/mobile/payhome` : `${PC_PAY}?site_preference=NORMAL`
                return next(orderInterrupted([{type: 'REDIRECT', url, reason}]))
            } else if (code === ApigwResponse.NOT_AGREE) {
                const url = isAnonymousUser
                    ? `${PAY_M}/mobile/foreign/join?url=${encodeURIComponent(window.location.href)}`
                    : isMobileDisplay
                      ? `${PAY_M}/mobile/agree?url=${encodeURIComponent(window.location.href)}`
                      : `${PC_PAY}/agree?url=${encodeURIComponent(window.location.href)}&payAgreeCurl=cancelURL`

                return next(
                    orderInterrupted([
                        {
                            type: 'REDIRECT',
                            url,
                            reason,
                        },
                    ]),
                )
            } else if (code === ApigwResponse.TRANSACTION_STOP) {
                transactionStopHandler?.({next, action, isMobileDisplay})
            } else if (code === ApigwResponse.LEAVE) {
                return next(
                    orderInterrupted([
                        {
                            type: 'REDIRECT',
                            url: `${PAY_M}/mobile/memberJoinImpossible`,
                            reason,
                        },
                    ]),
                )
            } else if (code === ApigwResponse.NOT_REAL_NAME) {
                if (shouldCheckAnonymousUser && isAnonymousUser) {
                    return next(action)
                }

                const {fallbackUrl} = commonSelector(state)
                return next(
                    orderInterrupted([
                        {
                            type: 'REDIRECT',
                            url: getRealNameCheckPage(window.location.href, fallbackUrl),
                        },
                    ]),
                )
            } else if (code === ApigwResponse.NO_LOGIN_INFO) {
                if (noLoginInfoHandler) {
                    noLoginInfoHandler?.({next, action, state: getState()})
                } else {
                    return next(
                        orderInterrupted([
                            {
                                type: 'REDIRECT',
                                url: getLoginUrl({
                                    svcType,
                                }),
                            },
                        ]),
                    )
                }
            } else if (code === ApigwResponse.MEMBER_INFO_RETRIEVAL_FAILED) {
                next(
                    orderInterrupted([
                        {
                            type: 'ALERT',
                            message: '치명적인 오류가 발생하였습니다. 잠시 후 다시 시도해 주세요.',
                        },
                        /** [고민] 페이 홈 이동 or 뒤로가기 */
                        {type: 'REDIRECT'},
                        // {type: 'BACK', shouldClose: true},
                    ]),
                )
            }

            return undefined
        }
        if (
            isRejected(action) &&
            action.error?.name === GwErrorName &&
            !exceptionAuthActionTypes.includes(action.type)
        ) {
            const error = getGwErrorMessage<{userAuthStatus?: UserAuthStatusType}>(action.error.message)

            if (!error) {
                /** ignore error */
                return
            }

            return authCallback(error)
        } else if (isApiAsyncThunkAuthRejected(action) && !exceptionAuthActionTypes.includes(action.type)) {
            return authCallback(action.payload.value.response)
        } else if (isRejected(action) && validationFailureCodes.includes(action.payload?.code)) {
            validationFailureHandler?.({next, action, state: getState()})
        }
        // 판매자 회원 검증
        else if (isRejected(action) && action.payload?.code === ReasonType.UNREGISTERED_SELLER) {
            next(
                setFailureException({
                    type: ErrorType.AUTHORIZATION_FAILED,
                    code: ReasonType.UNREGISTERED_SELLER,
                }),
            )
        }

        return next(action)
    }

export default authCreator
